import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Save, RotateCcw, ExternalLink, Plus, Trash2 } from "lucide-react";

// Redigerbara textfält per variant (etikett + nyckel). Steg hanteras separat.
const FIELDS = {
  landing: [
    ["badge", "Topp-etikett (badge)", "input"],
    ["hero_heading", "Huvudrubrik (hero)", "area"],
    ["hero_subtitle", "Underrubrik / säljtext", "area"],
    ["cta_label", "Knapptext (CTA)", "input"],
  ],
  candidate: [
    ["heading", "Rubrik", "input"],
    ["intro", "Ingress", "area"],
    ["ai_card_title", "AI-kort: rubrik", "input"],
    ["ai_card_text", "AI-kort: text", "area"],
    ["steps_heading", "Rubrik ovanför stegen", "input"],
    ["vagledning_tip_title", "Tips 'Sök jobb': rubrik", "input"],
    ["vagledning_tip_text", "Tips 'Sök jobb': text", "area"],
    ["review_tip_title", "Tips 'Granska': rubrik", "input"],
    ["review_tip_text", "Tips 'Granska': text", "area"],
    ["transparency_title", "AI-transparens: rubrik", "input"],
    ["transparency_text", "AI-transparens: text", "area"],
    ["pnr_title", "Personnummer-varning: rubrik", "input"],
    ["pnr_text", "Personnummer-varning: text", "area"],
    ["done_title", "Klart-ruta (alla steg klara): rubrik", "input"],
    ["done_text", "Klart-ruta (alla steg klara): text", "area"],
    ["done_cta_label", "Klart-ruta: knapptext", "input"],
  ],
  employer: [
    ["heading", "Rubrik", "input"],
    ["intro", "Ingress", "area"],
    ["ai_card_title", "Matchnings-kort: rubrik", "input"],
    ["ai_card_text", "Matchnings-kort: text", "area"],
    ["steps_heading", "Rubrik ovanför stegen", "input"],
    ["privacy_title", "Integritet: rubrik", "input"],
    ["privacy_text", "Integritet: text", "area"],
    ["tip_title", "Smarta tips: rubrik", "input"],
    ["tip_text", "Smarta tips: text", "area"],
    ["transparency_title", "AI-transparens: rubrik", "input"],
    ["transparency_text", "AI-transparens: text", "area"],
  ],
};

const VARIANTS = [
  { key: "landing", label: "Startsida", preview: "/", pageLabel: "startsidan" },
  { key: "candidate", label: "Kandidatguide", preview: "/sa-funkar-det", pageLabel: "guiden 'Så funkar det'" },
  { key: "employer", label: "Arbetsgivarguide", preview: "/sa-funkar-det?for=arbetsgivare", pageLabel: "arbetsgivarguiden" },
];

// Vilken lista varje variant har (steg resp. fördelar-kort) och dess etikett.
const LIST_META = {
  landing: { key: "features", label: "Fördelar (kort)" },
  candidate: { key: "steps", label: "Steg" },
  employer: { key: "steps", label: "Steg" },
};

export default function AdminGuide() {
  const [content, setContent] = useState(null);
  const [variant, setVariant] = useState("landing");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = () => api.get("/guide-content").then((r) => { setContent(r.data); setDirty(false); }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!dirty) return;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const listKey = LIST_META[variant].key;
  const setVal = (key, val) => { setDirty(true); setContent((c) => ({ ...c, [variant]: { ...c[variant], [key]: val } })); };
  const setStep = (i, field, val) => {
    setDirty(true);
    setContent((c) => {
      const items = [...(c[variant][listKey] || [])];
      items[i] = { ...items[i], [field]: val };
      return { ...c, [variant]: { ...c[variant], [listKey]: items } };
    });
  };

  const addStep = () => {
    setDirty(true);
    setContent((c) => {
      const items = [...(c[variant][listKey] || []), { title: "", text: "" }];
      return { ...c, [variant]: { ...c[variant], [listKey]: items } };
    });
  };

  const removeStep = (i) => {
    if (!window.confirm("Ta bort det här steget? Kom ihåg att spara efteråt.")) return;
    setDirty(true);
    setContent((c) => {
      const items = (c[variant][listKey] || []).filter((_, idx) => idx !== i);
      return { ...c, [variant]: { ...c[variant], [listKey]: items } };
    });
  };

  const switchVariant = (key) => {
    if (key === variant) return;
    if (dirty && !window.confirm("Du har osparade ändringar. Byt sida utan att spara? Ändringarna försvinner.")) return;
    setVariant(key);
  };

  const reload = () => {
    if (dirty && !window.confirm("Läsa om från servern? Dina osparade ändringar försvinner.")) return;
    load();
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/guide-content", { content });
      setContent(data);
      setDirty(false);
      toast.success("Sparat! Ändringarna syns direkt på sidan.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!content) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const v = content[variant] || {};
  const meta = VARIANTS.find((x) => x.key === variant);
  const previewUrl = meta?.preview;

  return (
    <div className="space-y-5 pb-24" data-testid="admin-guide">
      <div className="flex flex-wrap items-center gap-2">
        {VARIANTS.map((x) => (
          <Button key={x.key} variant={variant === x.key ? "default" : "outline"} className="rounded-full"
            onClick={() => switchVariant(x.key)} data-testid={`guide-variant-${x.key}`}>{x.label}</Button>
        ))}
      </div>

      <div className="rounded-xl border border-primary/25 bg-primary/[0.04] px-4 py-3 flex flex-wrap items-center justify-between gap-2 text-sm">
        <span className="text-muted-foreground">
          Du redigerar <strong className="text-foreground">{meta?.label}</strong> – visas på{" "}
          <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="text-primary font-medium hover:underline" data-testid="guide-preview-link">
            {meta?.pageLabel} <ExternalLink className="h-3.5 w-3.5 inline" />
          </a>
        </span>
        {dirty && <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-800 px-3 py-1 text-xs font-medium" data-testid="guide-dirty-badge"><Save className="h-3 w-3" /> Osparade ändringar</span>}
      </div>

      <Card className="rounded-2xl border-border p-5 md:p-6 space-y-5">
        {FIELDS[variant].map(([key, label, kind]) => (
          <div key={key} className="space-y-1.5">
            <Label>{label}</Label>
            {kind === "area"
              ? <Textarea rows={3} value={v[key] || ""} onChange={(e) => setVal(key, e.target.value)} data-testid={`guide-field-${key}`} />
              : <Input value={v[key] || ""} onChange={(e) => setVal(key, e.target.value)} data-testid={`guide-field-${key}`} />}
          </div>
        ))}
      </Card>

      <Card className="rounded-2xl border-border p-5 md:p-6">
        <div className="font-head font-semibold mb-3">{LIST_META[variant].label} ({(v[listKey] || []).length})</div>
        <div className="space-y-5">
          {(v[listKey] || []).map((s, i) => (
            <div key={i} className="rounded-xl border border-border p-4 space-y-2" data-testid={`guide-step-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Steg {i + 1}</span>
                <Button variant="ghost" size="sm" className="rounded-full text-destructive hover:text-destructive hover:bg-destructive/10 h-8"
                  onClick={() => removeStep(i)} data-testid={`guide-step-remove-${i}`}>
                  <Trash2 className="h-4 w-4 mr-1" /> Ta bort
                </Button>
              </div>
              <div className="space-y-1.5">
                <Label>{i + 1} – rubrik</Label>
                <Input value={s.title || ""} onChange={(e) => setStep(i, "title", e.target.value)} data-testid={`guide-step-title-${i}`} />
              </div>
              <div className="space-y-1.5">
                <Label>{i + 1} – text</Label>
                <Textarea rows={3} value={s.text || ""} onChange={(e) => setStep(i, "text", e.target.value)} data-testid={`guide-step-text-${i}`} />
              </div>
            </div>
          ))}
        </div>
        <Button variant="outline" className="rounded-full mt-5" onClick={addStep} data-testid="guide-step-add">
          <Plus className="h-4 w-4 mr-1.5" /> Lägg till {LIST_META[variant].label.toLowerCase().includes("steg") ? "steg" : "kort"}
        </Button>
      </Card>

      {/* Sticky spar-rad så knappen alltid syns */}
      <div className="sticky bottom-0 z-10 -mx-1 mt-6 rounded-xl border border-border bg-white/95 backdrop-blur px-4 py-3 flex items-center justify-between gap-3 shadow-lg">
        <Button variant="ghost" className="rounded-full text-muted-foreground" onClick={reload} data-testid="guide-reload-btn"><RotateCcw className="h-4 w-4 mr-1.5" /> Läs om (kasta ändringar)</Button>
        <div className="flex items-center gap-3">
          {dirty && <span className="text-xs text-amber-700 hidden sm:inline">Kom ihåg att spara</span>}
          <Button className="rounded-full" onClick={save} disabled={saving || !dirty} data-testid="guide-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} {dirty ? "Spara ändringar" : "Sparat"}
          </Button>
        </div>
      </div>
    </div>
  );
}
